import { SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'

import { useCallMutation } from '@/common/api/client'
import { apiPostFetcher, apiPutFetcher } from '@/common/api/fetcher'
import {
  CreateW2MasterResponse,
  CreateW2MasterVars,
  UpdateW2MasterResponse,
  UpdateW2MasterVars
} from '@/common/api/params/w2Master'
import { ApiPath } from '@/common/api/path'
import { useFormErrorMessages, useMessageStates } from '@/common/app/message'
import { CSV_FORMATS } from '@/common/app/modules/csv'
import { masterTypeName } from '@/common/app/modules/master'
import { w2MasterEditPath, w2MasterListPath } from '@/common/app/path'
import { useShowSavedToast } from '@/common/app/toast'
import { MasterType, W2MasterData, W2_MASTER_CONFIGS } from '@/common/model/master/w2Master'
import { preventKeyDown } from '@/common/util/keyboard'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import FormLabel from '@/components/view/atoms/form/FormLabel'
import Heading from '@/components/view/atoms/heading/Heading'
import { FieldType, TextBoxSize } from '@/components/view/atoms/textfield/TextField'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import FormRow from '@/components/view/layouts/form/FormRow'
import Content from '@/components/view/layouts/page/Content'
import PageBottom from '@/components/view/layouts/page/PageBottom'
import TopBar, { TopBarStatus } from '@/components/view/molecules/bar/TopBar'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import {
  convertMasterSchemaToUpdateVars,
  convertMasterSchemaToVars,
  convertMasterToSchema,
  makeMasterSchema
} from '@/components/container/form/modules/master/master/masterSchema'
import FormError from '@/components/container/form/widgets/FormError'
import FormMessageList from '@/components/container/form/widgets/FormMessageList'
import FormTextField from '@/components/container/form/widgets/FormTextField'
import { useAppHeaderHiding } from '@/components/container/layout/AppHeaderProvider'

interface Props {
  siteId: number
  masterType: MasterType
  master?: W2MasterData
  onRefetch?(): void
}

const MasterForm = ({ siteId, masterType, master, onRefetch }: Props) => {
  const navigate = useNavigate()

  const csvColumnDescMap = Object.fromEntries(
    (CSV_FORMATS.find((f) => f.type === masterType)?.columns ?? []).map((c) => [c.name, c.desc])
  )

  const masterSchema = makeMasterSchema(masterType)
  type MasterFormSchemaType = z.infer<typeof masterSchema>

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isDirty },
    reset
  } = useForm<MasterFormSchemaType>({
    resolver: zodResolver(makeMasterSchema(masterType)),
    values: master
      ? convertMasterToSchema(master)
      : W2_MASTER_CONFIGS[masterType].csvHeaders.reduce(
          (values, header) => ({ ...values, [header]: '' }),
          {} as Record<string, string>
        ),
    mode: 'onChange'
  })

  useAppHeaderHiding(isDirty)
  const [messages, setMessages] = useMessageStates()
  useFormErrorMessages(errors, setMessages, {})
  const showSaved = useShowSavedToast({ editing: !!master })

  const handleSuccess = () => {
    showSaved(masterTypeName(masterType))
    master && onRefetch ? onRefetch() : navigate(w2MasterListPath(siteId, masterType))
  }

  const { trigger: createTrigger, isMutating: isCreating } = useCallMutation<
    CreateW2MasterResponse,
    CreateW2MasterVars
  >(ApiPath.CREATE_W2_MASTER, { onSuccess: handleSuccess }, apiPostFetcher)

  const { trigger: updateTrigger, isMutating: isUpdating } = useCallMutation<
    UpdateW2MasterResponse,
    UpdateW2MasterVars
  >(ApiPath.UPDATE_W2_MASTER, { onSuccess: handleSuccess }, apiPutFetcher)

  const isSubmitting = isCreating || isUpdating

  const onSubmit: SubmitHandler<MasterFormSchemaType> = (data) =>
    master
      ? updateTrigger(convertMasterSchemaToUpdateVars(data, siteId, masterType, master.id))
      : createTrigger(convertMasterSchemaToVars(data, siteId, masterType))

  return (
    <form onSubmit={handleSubmit(onSubmit)} onKeyDown={preventKeyDown}>
      <PageTitle
        title={masterTypeName(masterType) + (master ? '詳細' : '新規作成')}
        rightNode={
          <Button
            type='submit'
            size='large'
            color={ButtonColor.PRIMARY_BRAND}
            loading={isSubmitting}
          >
            {master ? '保存する' : '登録する'}
          </Button>
        }
      />
      {isDirty && (
        <TopBar
          status={master ? TopBarStatus.CHANGE : TopBarStatus.NEW}
          captionText={
            master
              ? '未反映の変更があります'
              : '登録されていない新しい' + masterTypeName(masterType)
          }
          onCancel={() => reset(master ? convertMasterToSchema(master) : {})}
          loading={isSubmitting}
        />
      )}
      <FormMessageList messageStates={messages} setMessageStates={setMessages} />
      <Content>
        <Area noBorder>
          <Heading>基本情報</Heading>
          <AreaContent>
            {W2_MASTER_CONFIGS[masterType].csvHeaders.map((header) => (
              <FormRow key={header}>
                <FormLabel
                  required={W2_MASTER_CONFIGS[masterType].requiredCsv.includes(header)}
                  id={header}
                  help={csvColumnDescMap[header]}
                >
                  {header}
                </FormLabel>
                <FormRow direction={'vertical'} gap={2}>
                  <FormTextField
                    register={register(header)}
                    fieldType={FieldType.REQUIRED}
                    error={!!errors[header]?.message}
                    size={TextBoxSize.FULL}
                    placeholder={header}
                  />
                  <FormError message={errors[header]?.message} />
                </FormRow>
              </FormRow>
            ))}
          </AreaContent>
        </Area>
      </Content>
      <PageBottom>
        <Button type='submit' size='large' color={ButtonColor.PRIMARY_BRAND} loading={isSubmitting}>
          {master ? '保存する' : '登録する'}
        </Button>
      </PageBottom>
    </form>
  )
}

export default MasterForm
